import React from 'react';
import { observer } from 'mobx-react';
import { computed, reaction } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import Checkbox from 'components/checkbox';
import { noop } from 'lodash';
import { isPresent, pixify, isBlank } from '@seedlang/utils';
import Text from 'components/text';
import VocabListWord from 'components/vocab/vocab_list_word';
import Paginator from 'components/paginator';
import Alert from 'components/alert';
import { AppUI, VocabUI, DictionaryNavigation } from '@seedlang/state';
import cx from 'classnames';
import Spinner from 'components/spinner';
import AddToReviews from 'components/vocab/add_to_reviews';

const Wrapper = styled.div`
  padding: ${props => props.removePadding ? 0 : '0 10px 10px'};
  background: white;
  box-sizing: border-box;
`;

const HeaderRow = styled.div`
  background: #636363;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  height: 30px;
`;

const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  width: ${props => props.width};
  flex: ${props => props.expand ? 1 : null};
  padding: 0 10px;
  border-right: 1px solid white;
  height: 30px;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  color: #FFF;
  i {
    color: #FFF;
    text-decoration: none;
    margin-right: 4px;
  }
  .fa-check {
    margin-right: 0;
  }
`;

const ToggleLink = styled.span`
  cursor: pointer;
  text-decoration: underline;
  hover {
    text-decoration: none;
  }
`;

const RowsWrapper = styled.div`
  height: ${props => props.height};
  overflow-y: ${props => props.isMobile ? 'auto' : 'scroll'};
  padding-top: 2px;
`;

const DidYouMeanQueryButton = styled.button`
  background: transparent;
  border: none;
  color: inherit;
  font: inherit;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin: 0;
`;

@observer
class VocabList extends React.Component {

  @computed get dictionaryNavigation() {
    return DictionaryNavigation.fromStore(this.props.wordStore, this.props.grammarSection ? {} : {vocab_list: true});
  }

  componentDidMount() {
    AppUI.createEvent('vocab - view dictionary', { source: this.eventSource });
    this.disposePageTracking = reaction(
      () => {
        const store = this.props.wordStore;
        if (this.eventSource !== 'dictionary' || this.showListSpinner || store.requestCounter > 0 || !store.hasIndexData) {
          return null;
        }
        return store.indexData;
      },
      results => {
        if (!results || results === this.lastTrackedResults) return;
        this.lastTrackedResults = results;
        const store = this.props.wordStore;
        const properties = {
          ...this.eventProperties,
          has_search_query: isPresent(store.filters.target_text),
        };
        const pageKey = JSON.stringify({
          ...properties,
          sort: store.sort,
          filters: store.filters,
        });
        if (pageKey !== this.lastTrackedPageKey) {
          this.lastTrackedPageKey = pageKey;
          AppUI.createEvent('vocab - view results page', properties);
        }
      },
      { fireImmediately: true },
    );
  }

  componentWillUnmount() {
    this.disposePageTracking?.();
  }

  @computed get eventSource() {
    return this.props.analyticsSource || (this.props.grammarSection ? 'grammar' : 'dictionary');
  }

  @computed get eventProperties() {
    return {
      source: this.eventSource,
      language_id: AppUI.targetLanguageId,
      page: Number(this.props.wordStore.page) || 1,
      page_size: this.props.wordStore.limit,
      has_search_query: isPresent(this.props.searchQuery),
    };
  }

  onPageLoaded = () => {
    if (this.eventSource === 'dictionary') {
      AppUI.createEvent('vocab - paginate', this.eventProperties);
    }
  }

  onToggleAddWordToDeck = (word, params) => {
    const selected = !this.props.selectedWordIds.includes(word.id);
    this.props.onToggleAddWordToDeck(word, params);
    if (this.eventSource === 'dictionary' && AppUI.user.isMember) {
      AppUI.createEvent('vocab - toggle word selection', {
        ...this.eventProperties,
        word_id: word.id,
        selected,
      });
    }
  }

  onToggleAddAllToDeck = () => {
    const selected = !this.props.allToggleSelected;
    this.props.onToggleAddAllToDeck();
    if (this.eventSource === 'dictionary') {
      AppUI.createEvent('vocab - toggle select all', {
        ...this.eventProperties,
        selected,
        visible_word_count: this.props.wordStore.indexData.length,
      });
    }
  }

  onToggleLearned = word => {
    const learned = !word.learned;
    this.props.onToggleLearned(word);
    if (this.eventSource !== 'review_create') {
      AppUI.createEvent('vocab - toggle learned', {
        ...this.eventProperties,
        word_id: word.id,
        learned,
      });
    }
  }

  @computed get mobileHeight() {
    if (isPresent(this.props.searchQuery)) {
      return AppUI.layout.mobileContentHeight - 220;
    }
    return AppUI.layout.mobileContentHeight - 275;
  }

  @computed get searchHighlightsById() {
    return this.props.wordStore?.metaData?.searchHighlightsById || {};
  }

  @computed get childTargetExactMatchById() {
    const matches = this.props.wordStore?.metaData?.childTargetExactMatches || [];
    return matches.reduce((lookup, item) => {
      if (item?.id) {
        lookup[item.id] = item.text;
      }
      return lookup;
    }, {});
  }

  @computed get searchSuggestion() {
    const suggestions = this.props.wordStore?.metaData?.suggestions || [];
    return suggestions.find(item => isPresent(item));
  }

  @computed get showPaginationSpinner() {
    return !this.props.showWordListSpinner && this.props.wordStore?.requestCounter > 0 && !this.props.wordStore?.hasIndexData;
  }

  @computed get showListSpinner() {
    return this.props.showWordListSpinner || this.showPaginationSpinner;
  }

  @computed get showNoResultsBanner() {
    return this.props.showNoResultsMessage && !this.showPaginationSpinner;
  }

  onUseSearchSuggestion = () => {
    if (isBlank(this.searchSuggestion)) {
      return;
    }
    const onUpdateSearchQuery = this.props.onUpdateSearchQuery || this.props.onUpdateWordSearchQuery;
    if (typeof onUpdateSearchQuery !== 'function') {
      return;
    }
    onUpdateSearchQuery(this.searchSuggestion, this.searchSuggestion.length);
  }

  render() {
    const showHeaders = this.props.showHeaders === undefined ? true : this.props.showHeaders;

    return (
      <Wrapper
        removePadding={this.props.removeMobileEdgePadding && AppUI.layout.isMobile}
      >
        {
          isPresent(this.props.reviewDeckOptions) &&
            <AddToReviews
              reviewDeckOptions={this.props.reviewDeckOptions}
              deckId={this.props.deckId}
              onSetDeckId={this.props.onSetDeckId}
              cardTypeOptions={this.props.cardTypeOptions}
              cardTypeId={this.props.cardTypeId}
              onSetCardTypeId={this.props.onSetCardTypeId}
              onCreateReviews={this.props.onCreateReviews}
              deckButtonDisabled={this.props.deckButtonDisabled}
              selectedWordsExcludedFromGenderTrainer={this.props.selectedWordsExcludedFromGenderTrainer}
              selectedWordsExcludedFromPluralTrainer={this.props.selectedWordsExcludedFromPluralTrainer}
              selectedWordsExcludedFromConjugationTrainer={this.props.selectedWordsExcludedFromConjugationTrainer}
              showReviewsCreatedMessage={this.props.createReviewsFinished || this.props.createReviewsIsSlow}
              selectedWordIds={this.props.selectedWordIds}
            />
        }
        {
          this.props.totalCount === 0 &&
            <Alert>
              There are no matching results for this search.
            </Alert>
        }
        {
          showHeaders && !this.showNoResultsBanner &&
          <HeaderRow>
            {
              !this.props.grammarSection &&
                <HeaderCell
                  width='40px'
                >
                  {
                    this.props.showColumnCheckbox &&
                    <Checkbox
                      background='#d0d0d0'
                      selectedBackground='#232323'
                      onClick={this.onToggleAddAllToDeck}
                      value={this.props.allToggleSelected}
                    />
                  }
                </HeaderCell>
            }
            {
              !this.props.grammarSection && !AppUI.layout.isMobile && AppUI.layout.leftColumnWidth > 700 &&
                <HeaderCell
                  clickable={isBlank(this.props.searchQuery)}
                  width='60px'
                  onClick={() => isPresent(this.props.searchQuery) ? noop : this.props.onClickSort('frequency_ranking')}
                >
                  {
                    (!this.props.sort || this.props.sort === 'frequency_ranking') && isBlank(this.props.searchQuery) &&
                      <i className={cx('fa', {
                        'fa-caret-down': !this.props.sortDirection || this.props.sortDirection === 'asc',
                        'fa-caret-up': this.props.sortDirection === 'desc',
                      })}
                      />
                  }
                  <Text
                    color='white'
                    fontSize='10px'
                    lineHeight='11px'
                    textDecoration={isBlank(this.props.searchQuery) ? 'underline' : ''}
                    bold
                  >
                    Freq
                  </Text>
                </HeaderCell>
            }
            <HeaderCell
              clickable={!AppUI.layout.isMobile && !this.props.grammarSection && isBlank(this.props.searchQuery)}
              expand={!this.props.showSource}
              width={this.props.showSource ? '200px' : null}
              onClick={() => AppUI.layout.isMobile || this.props.grammarSection || isPresent(this.props.searchQuery) ? noop : this.props.onClickSort('target_text')}
            >
              {
                !AppUI.layout.isMobile && !this.props.grammarSection && this.props.sort === 'target_text' && isBlank(this.props.searchQuery) &&
                  <i className={cx('fa', {
                    'fa-caret-down': !this.props.sortDirection || this.props.sortDirection === 'asc',
                    'fa-caret-up': this.props.sortDirection === 'desc',
                  })}
                  />
              }
              <Text
                color='white'
                fontSize='10px'
                lineHeight='11px'
                textDecoration={AppUI.layout.isMobile || isPresent(this.props.searchQuery) ? null : 'underline'}
                bold
              >
                Word
              </Text>
            </HeaderCell>
            {
              this.props.showSource &&
                <HeaderCell
                  expand
                >
                  <Text
                    color='white'
                    fontSize='10px'
                    lineHeight='11px'
                    bold
                  >
                    <span>Translation </span>
                    <span>(</span>
                    <ToggleLink
                      onClick={this.props.onToggleExpandTranslation}
                    >
                      {this.props.expandTranslation ? 'hide' : 'show'}
                    </ToggleLink>
                    <span>)</span>
                  </Text>
                </HeaderCell>
            }
            <HeaderCell
              width='70'
            >
              <Text
                color='white'
                fontSize='10px'
                lineHeight='11px'
                bold
              >
                Progress
              </Text>
            </HeaderCell>
            <HeaderCell
              width='65px'
            >
              <Text
                color='white'
                fontSize='10px'
                lineHeight='11px'
                bold
              >
                Learned
              </Text>
            </HeaderCell>
          </HeaderRow>
        }
        <RowsWrapper
          height={AppUI.layout.isMobile ? pixify(this.mobileHeight) : null}
          isMobile={AppUI.layout.isMobile}
        >
          {
            this.showListSpinner &&
              <Spinner
                blue
              />
          }
          {
            !this.showListSpinner && this.props.wordStore.indexData.map(item => {
              return (
                <VocabListWord
                  key={item.id}
                  dictionaryNavigation={this.dictionaryNavigation}
                  setWord={this.props.setWord}
                  onSelectWord={this.props.onSelectWord}
                  showExpanded={isPresent(this.props.searchQuery) && !this.props.isMobile}
                  word={item}
                  user={AppUI.user}
                  wordType={this.props.wordType}
                  selectedWordIds={this.props.selectedWordIds}
                  isPro={this.props.user.isMember}
                  onToggleAddWordToDeck={this.onToggleAddWordToDeck}
                  onToggleLearned={this.onToggleLearned}
                  isMobile={this.props.isMobile}
                  allowSelection={AppUI.user.isMember}
                  showSource={this.props.showSource}
                  afterSubmitSourceInput={this.props.afterSubmitSourceInput}
                  expandTranslation={this.props.expandTranslation}
                  selectedCardTypeId={this.props.cardTypeId}
                  grammarSection={this.props.grammarSection}
                  searchQuery={this.props.searchQuery}
                  searchHighlights={this.searchHighlightsById[item.id]}
                  childTargetExactMatchText={this.childTargetExactMatchById[item.id]}
                />
              );
            })
          }
          {
            !this.showListSpinner && this.props.wordStore.hasIndexData && this.props.wordStore.pagesCount > 1 &&
            <Paginator
              store={this.props.wordStore}
              totalCount={this.props.wordStore.totalCount}
              filters={this.props.filters}
              ids={this.props.ids}
              queryStrings={this.props.grammarSection ? {} : {vocab_list: true}}
              clearOnPagination
              onPagination={() => VocabUI.allToggleSelected = false}
              onPageLoaded={this.onPageLoaded}
              updateUrl={!this.props.grammarSection && isBlank(this.props.searchQuery)}
              onClickScrollToTop
            />
          }
          {
            this.showNoResultsBanner &&
            <Alert
              textAlign='center'
              width='100%'
            >
              <span>There are no results to display.</span>
              {
                isPresent(this.searchSuggestion) &&
                <>
                  <span> Did you mean: </span>
                  <DidYouMeanQueryButton type='button' onClick={this.onUseSearchSuggestion}>
                    {this.searchSuggestion}
                  </DidYouMeanQueryButton>
                  <span>?</span>
                </>
              }
            </Alert>
          }
        </RowsWrapper>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(VocabList);
